import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  fetchTattvaIndex,
  fetchTattvaNode,
  saveTattvaNode,
  submitTattvaNode,
  publishTattvaNode,
} from "@/lib/tattva/tattva-api";
import { GraphVisualizer } from "@/components/tattva/GraphVisualizer";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { Loader2, Plus, Save, Send, CheckCircle, Trash2 } from "lucide-react";

const NODE_TYPES = [
  "deity", "festival", "vrata", "story", "sadhana", "mantra",
  "chakra", "element", "guna", "planet", "nakshatra", "psychology", "principle",
];

function emptyNode(id = "chakra:new") {
  return { id, type: "chakra", title: "", attributes: {}, edges: [] };
}

export function TattvaGraphAdmin() {
  const { canEditLibrary, canPublish } = useSacredAdmin();
  const [index, setIndex] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [node, setNode] = useState(emptyNode());
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [newId, setNewId] = useState("");

  const loadIndex = async () => {
    setLoading(true);
    try {
      const nodes = await fetchTattvaIndex();
      setIndex(nodes);
      if (!selectedId && nodes.length) setSelectedId(nodes[0].id);
    } catch {
      setIndex([]);
    }
    setLoading(false);
  };

  const loadNode = async (id) => {
    if (!id) return;
    setMessage("");
    try {
      const { node: n, draft } = await fetchTattvaNode(id);
      setNode(draft || n || emptyNode(id));
      setSelectedId(id);
    } catch {
      setNode(emptyNode(id));
      setSelectedId(id);
    }
  };

  useEffect(() => {
    if (canEditLibrary) loadIndex();
  }, [canEditLibrary]);

  useEffect(() => {
    if (selectedId) loadNode(selectedId);
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return index;
    return index.filter(
      (n) =>
        n.id.toLowerCase().includes(q) ||
        (n.title || "").toLowerCase().includes(q) ||
        n.type.includes(q)
    );
  }, [index, filter]);

  const previewNodes = useMemo(() => {
    const targets = node.edges?.map((e) => e.to) || [];
    const related = index
      .filter((n) => targets.includes(n.id))
      .map((n) => ({ id: n.id, type: n.type, title: n.title }));
    return [{ id: node.id, type: node.type, title: node.title }, ...related];
  }, [node, index]);

  const previewEdges = useMemo(
    () => (node.edges || []).map((e) => ({ from: node.id, to: e.to, relation: e.relation })),
    [node]
  );

  const persist = async (opts) => {
    setSaving(true);
    setMessage("");
    try {
      if (opts.submit) await submitTattvaNode(node.id, node);
      else if (opts.publish) await publishTattvaNode(node.id, node);
      else await saveTattvaNode(node.id, node, { draftOnly: true });
      setMessage(opts.submit ? "Submitted for approval" : opts.publish ? "Published" : "Draft saved");
      await loadIndex();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const addEdge = () => {
    setNode((n) => ({
      ...n,
      edges: [...(n.edges || []), { to: "", relation: "related_to" }],
    }));
  };

  const updateEdge = (i, field, value) => {
    setNode((n) => {
      const edges = [...(n.edges || [])];
      edges[i] = { ...edges[i], [field]: value };
      return { ...n, edges };
    });
  };

  const removeEdge = (i) => {
    setNode((n) => ({
      ...n,
      edges: (n.edges || []).filter((_, j) => j !== i),
    }));
  };

  const createNode = () => {
    const id = newId.trim().toLowerCase().replace(/\s+/g, "_");
    if (!id.includes(":")) {
      setMessage('Use format type:slug (e.g. deity:rama)');
      return;
    }
    setSelectedId(id);
    setNode(emptyNode(id));
    setNewId("");
  };

  if (!canEditLibrary) {
    return <p className="text-sm text-muted-foreground">Tattva graph requires admin or editor role.</p>;
  }

  if (loading && !index.length) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-6" data-testid="tattva-graph-admin">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">New graph node</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label>Node id (type:slug)</Label>
            <Input placeholder="deity:rama" value={newId} onChange={(e) => setNewId(e.target.value)} />
          </div>
          <Button type="button" onClick={createNode}>
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-[240px_1fr] gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Nodes ({filtered.length})</CardTitle>
            <Input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} className="mt-2" />
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-y-auto space-y-1 p-2">
            {filtered.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`w-full text-left text-xs px-2 py-1.5 rounded ${
                  selectedId === n.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                }`}
                onClick={() => setSelectedId(n.id)}
              >
                <span className="font-mono block truncate">{n.id}</span>
                <span className="opacity-80">{n.type}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Edit node</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Id</Label>
                  <Input
                    value={node.id}
                    onChange={(e) => setNode((n) => ({ ...n, id: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={node.type} onValueChange={(v) => setNode((n) => ({ ...n, type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NODE_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Title</Label>
                  <Input value={node.title || ""} onChange={(e) => setNode((n) => ({ ...n, title: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Attributes (JSON)</Label>
                  <Textarea
                    rows={4}
                    value={JSON.stringify(node.attributes || {}, null, 2)}
                    onChange={(e) => {
                      try {
                        setNode((n) => ({ ...n, attributes: JSON.parse(e.target.value) }));
                      } catch {
                        /* typing */
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Edges</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addEdge}>
                    <Plus className="w-3 h-3 mr-1" /> Add edge
                  </Button>
                </div>
                <div className="space-y-2">
                  {(node.edges || []).map((e, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input
                        placeholder="target id"
                        value={e.to}
                        onChange={(ev) => updateEdge(i, "to", ev.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="relation"
                        value={e.relation}
                        onChange={(ev) => updateEdge(i, "relation", ev.target.value)}
                        className="flex-1"
                      />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeEdge(i)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {message && <p className="text-sm text-muted-foreground">{message}</p>}

              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={saving} onClick={() => persist({})}>
                  <Save className="w-4 h-4 mr-1" /> Save draft
                </Button>
                <Button type="button" variant="secondary" disabled={saving} onClick={() => persist({ submit: true })}>
                  <Send className="w-4 h-4 mr-1" /> Submit
                </Button>
                {canPublish && (
                  <Button type="button" variant="default" disabled={saving} onClick={() => persist({ publish: true })}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Publish
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Graph preview</CardTitle>
            </CardHeader>
            <CardContent>
              <GraphVisualizer nodes={previewNodes} edges={previewEdges} height={280} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
